import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { forkJoin, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { ComplexExerciseApiService } from "src/app/shared/services/exercise/complex-exercise-api.service";
import { FileService } from "src/app/shared/services/file/file.service";
import * as fromComplexExercise from "src/app/store/actions/complex-exercise.actions";

@Injectable()
export class ComplexExerciseEffect {
  loadComplexExerciseConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromComplexExercise.LOAD_COMPLEX_EXERCISE_CONFIG),
      switchMap((action: fromComplexExercise.LoadComplexExerciseConfig) =>
        this.complexExerciseApiService
          .getComplexConfig({
            courseId: action.payload.courseId,
            groupId: action.payload.groupId,
            exerciseId: action.payload.exerciseId,
          })
          .pipe(
            map(
              (config) =>
                new fromComplexExercise.LoadComplexExerciseConfigCompleted({
                  config,
                }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                new fromComplexExercise.LoadComplexExerciseConfigFailed({
                  errorStatus: error?.status,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadTeacherComplexExerciseData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromComplexExercise.LOAD_TEACHER_COMPLEX_EXERCISE_DATA),
      switchMap((action: fromComplexExercise.LoadTeacherComplexExerciseData) =>
        forkJoin([
          this.complexExerciseApiService.getComplexConfig({
            courseId: action.payload.courseId,
            groupId: action.payload.groupId,
            exerciseId: action.payload.exerciseId,
          }),
          this.complexExerciseApiService.getComplexSubmissions({
            courseId: action.payload.courseId,
            groupId: action.payload.groupId,
            exerciseId: action.payload.exerciseId,
          }),
        ]).pipe(
          map(
            ([config, submissions]) =>
              new fromComplexExercise.LoadTeacherComplexExerciseDataCompleted({
                config,
                submissions: submissions || [],
              }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              new fromComplexExercise.LoadTeacherComplexExerciseDataFailed({
                errorStatus: error?.status,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadComplexSubmissionsCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromComplexExercise.DOWNLOAD_COMPLEX_SUBMISSIONS_CSV),
      switchMap((action: fromComplexExercise.DownloadComplexSubmissionsCsv) =>
        this.complexExerciseApiService
          .getComplexSubmissionsCsv({
            courseId: action.payload.courseId,
            groupId: action.payload.groupId,
            exerciseId: action.payload.exerciseId,
          })
          .pipe(
            tap((response) => {
              const blob = response.body;
              if (!blob) {
                return;
              }

              const fileName =
                this.resolveFilenameFromHeaders(
                  response.headers.get("content-disposition"),
                ) || "complex_submissions.csv";

              const url = window.URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = fileName;
              document.body.appendChild(anchor);
              anchor.click();
              document.body.removeChild(anchor);
              window.URL.revokeObjectURL(url);
            }),
            map(() => new fromComplexExercise.DownloadComplexSubmissionsCsvCompleted()),
            catchError(() => of(new fromComplexExercise.DownloadComplexSubmissionsCsvFailed())),
          ),
      ),
    ),
  );

  downloadComplexBulkFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromComplexExercise.DOWNLOAD_COMPLEX_BULK_FILES),
      switchMap((action: fromComplexExercise.DownloadComplexBulkFiles) =>
        this.complexExerciseApiService
          .getComplexBulkDownload({
            courseId: action.payload.courseId,
            groupId: action.payload.groupId,
            exerciseId: action.payload.exerciseId,
          })
          .pipe(
            map((response) => {
              if (response?.s3_url) {
                this.fileService.downloadS3File(response.s3_url, response.filename);
                return new fromComplexExercise.DownloadComplexBulkFilesCompleted({
                  isGenerating: false,
                  tooltipKey: "COURSES.COURSE.EXERCISE.DOWNLOAD_ALL_STUDENTS_FILES",
                });
              }

              if (response?.is_generating) {
                return new fromComplexExercise.DownloadComplexBulkFilesCompleted({
                  isGenerating: true,
                  tooltipKey: "COURSES.COURSE.EXERCISE.COMPLEX_BULK_DOWNLOAD_GENERATING",
                });
              }

              return new fromComplexExercise.DownloadComplexBulkFilesFailed({
                tooltipKey: "COURSES.COURSE.EXERCISE.COMPLEX_BULK_DOWNLOAD_ERROR",
              });
            }),
            catchError(() =>
              of(
                new fromComplexExercise.DownloadComplexBulkFilesFailed({
                  tooltipKey: "COURSES.COURSE.EXERCISE.COMPLEX_BULK_DOWNLOAD_ERROR",
                }),
              ),
            ),
          ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly complexExerciseApiService: ComplexExerciseApiService,
    private readonly fileService: FileService,
  ) {}

  private resolveFilenameFromHeaders(
    contentDisposition: string | null,
  ): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1].replace(/['"]/g, "").trim());
    }

    const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (!plainMatch?.[1]) {
      return null;
    }

    return plainMatch[1].replace(/['"]/g, "").trim();
  }
}
